import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { 
  CreditCard, 
  Plus, 
  Edit,
  Trash2,
  Save,
  X,
  Check,
  DollarSign,
  Users,
  Settings,
  ToggleLeft,
  ToggleRight
} from 'lucide-react'
import api from '../lib/api'
import { formatCurrency } from '../lib/utils'
import toast from 'react-hot-toast'

export default function PlansPage() {
  const queryClient = useQueryClient()
  const [editingPlan, setEditingPlan] = useState(null)
  const [isCreating, setIsCreating] = useState(false)

  const { data: plans, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const response = await api.get('/admin/plans')
      return response.data.data // Backend returns { success, data }
    }
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      name: '',
      display_name: '',
      description: '',
      price: 0,
      billing_cycle: 'monthly',
      features_json: {
        max_users: 0,
        max_students: 0,
        max_sessions_per_month: 0,
        storage_mb: 0,
        features: []
      },
      is_active: true,
      sort_order: 0
    }
  })

  const createPlanMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/plans', data)
      return response.data.data // Backend returns { success, data }
    },
    onSuccess: () => {
      toast.success('Plan created successfully!')
      setIsCreating(false)
      reset()
      queryClient.invalidateQueries(['plans'])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create plan')
    }
  })

  const updatePlanMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/plans/${id}`, data)
      return response.data.data // Backend returns { success, data }
    },
    onSuccess: () => {
      toast.success('Plan updated successfully!')
      setEditingPlan(null)
      queryClient.invalidateQueries(['plans'])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update plan')
    }
  })

  const deletePlanMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/plans/${id}`)
      return response.data // Delete might not return data
    },
    onSuccess: () => {
      toast.success('Plan deleted successfully!')
      queryClient.invalidateQueries(['plans'])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete plan')
    }
  })

  const togglePlanStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.post(`/admin/plans/${id}/toggle-status`)
      return response.data // Toggle might not return data
    },
    onSuccess: () => {
      toast.success('Plan status updated!')
      queryClient.invalidateQueries(['plans'])
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update plan status')
    }
  })

  const handleCreate = () => {
    setIsCreating(true)
    setEditingPlan(null)
    reset()
  }

  const handleEdit = (plan) => {
    setEditingPlan(plan.id)
    setIsCreating(false)
    reset({
      name: plan.name,
      display_name: plan.display_name,
      description: plan.description,
      price: plan.price,
      billing_cycle: plan.billing_cycle,
      features_json: plan.features_json,
      is_active: plan.is_active,
      sort_order: plan.sort_order
    })
  }

  const handleCancel = () => {
    setIsCreating(false)
    setEditingPlan(null)
    reset()
  }

  const onSubmit = (data) => {
    if (isCreating) {
      createPlanMutation.mutate(data)
    } else {
      updatePlanMutation.mutate({ id: editingPlan, data })
    }
  }

  const handleDelete = (plan) => {
    if (window.confirm(`Are you sure you want to delete "${plan.display_name}"? This action cannot be undone.`)) {
      deletePlanMutation.mutate(plan.id)
    }
  }

  const handleToggleStatus = (plan) => {
    togglePlanStatusMutation.mutate(plan.id)
  }

  const features = watch('features_json.features') || []
  const newFeature = watch('newFeature') || ''

  const addFeature = () => {
    if (newFeature.trim()) {
      const currentFeatures = features
      const updatedFeatures = [...currentFeatures, newFeature.trim()]
      // Update the form value
      const currentForm = watch()
      reset({
        ...currentForm,
        features_json: {
          ...currentForm.features_json,
          features: updatedFeatures
        },
        newFeature: ''
      })
    }
  }

  const removeFeature = (index) => {
    const currentFeatures = features
    const updatedFeatures = currentFeatures.filter((_, i) => i !== index)
    const currentForm = watch()
    reset({
      ...currentForm,
      features_json: {
        ...currentForm.features_json,
        features: updatedFeatures
      }
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-muted rounded w-1/2 mb-4"></div>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded"></div>
                <div className="h-3 bg-muted rounded w-5/6"></div>
                <div className="h-3 bg-muted rounded w-4/6"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Subscription Plans</h1>
          <p className="text-muted-foreground">Manage pricing plans and features</p>
        </div>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Plan
        </button>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans?.map((plan) => {
          // Parse features_json if it's a string
          const features = typeof plan.features_json === 'string' 
            ? JSON.parse(plan.features_json) 
            : plan.features_json;
          
          return (
          <div key={plan.id} className="card p-6 relative">
            {plan.name === 'enterprise' && (
              <div className="absolute top-4 right-4">
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary/10 text-primary">
                  Popular
                </span>
              </div>
            )}
            
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold mb-2">{plan.display_name}</h3>
              <div className="flex items-center justify-center mb-2">
                <DollarSign className="h-6 w-6 text-muted-foreground" />
                <span className="text-3xl font-bold">
                  {plan.price}
                </span>
                <span className="text-muted-foreground ml-1">
                  /{plan.billing_cycle}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {plan.description}
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {features?.features?.map((feature, index) => (
                <div key={index} className="flex items-center">
                  <Check className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              )) || (
                <p className="text-sm text-muted-foreground">No features listed</p>
              )}
            </div>

            <div className="space-y-2 text-sm text-muted-foreground mb-6">
              {features?.max_users && (
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  <span>
                    {features.max_users === -1 
                      ? 'Unlimited users' 
                      : `${features.max_users} users`
                    }
                  </span>
                </div>
              )}
              {features?.max_students && (
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  <span>
                    {features.max_students === -1 
                      ? 'Unlimited students' 
                      : `${features.max_students} students`
                    }
                  </span>
                </div>
              )}
              {features?.storage_mb && (
                <div className="flex items-center">
                  <Settings className="h-4 w-4 mr-2" />
                  <span>
                    {features.storage_mb === -1 
                      ? 'Unlimited storage' 
                      : `${features.storage_mb} MB storage`
                    }
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <button
                  onClick={() => handleToggleStatus(plan)}
                  className="flex items-center gap-1"
                >
                  {plan.is_active ? (
                    <ToggleRight className="h-5 w-5 text-green-600" />
                  ) : (
                    <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                  )}
                  <span className={`text-xs font-medium ${
                    plan.is_active ? 'text-green-600' : 'text-muted-foreground'
                  }`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                </button>
              </div>
              <div className="flex gap-1">
                <button 
                  onClick={() => handleEdit(plan)}
                  className="btn btn-outline btn-sm"
                >
                  <Edit className="h-3 w-3" />
                </button>
                <button 
                  onClick={() => handleDelete(plan)}
                  className="btn btn-destructive btn-sm"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Create/Edit Plan Modal */}
      {(isCreating || editingPlan) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">
                  {isCreating ? 'Create New Plan' : 'Edit Plan'}
                </h2>
                <button onClick={handleCancel} className="btn btn-ghost btn-sm">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Plan Name *</label>
                    <input
                      {...register('name', { required: 'Plan name is required' })}
                      className="input mt-1"
                      placeholder="e.g., starter"
                    />
                    {errors.name && (
                      <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Display Name *</label>
                    <input
                      {...register('display_name', { required: 'Display name is required' })}
                      className="input mt-1"
                      placeholder="e.g., Starter Plan"
                    />
                    {errors.display_name && (
                      <p className="text-sm text-destructive mt-1">{errors.display_name.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    {...register('description')}
                    className="input mt-1"
                    rows={3}
                    placeholder="Plan description..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="label">Price *</label>
                    <input
                      {...register('price', { 
                        required: 'Price is required',
                        min: { value: 0, message: 'Price must be 0 or greater' }
                      })}
                      type="number"
                      step="0.01"
                      min="0"
                      className="input mt-1"
                    />
                    {errors.price && (
                      <p className="text-sm text-destructive mt-1">{errors.price.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">Billing Cycle *</label>
                    <select {...register('billing_cycle')} className="input mt-1">
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Sort Order</label>
                    <input
                      {...register('sort_order')}
                      type="number"
                      className="input mt-1"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Plan Limits</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-1">
                    <div>
                      <label className="text-xs text-muted-foreground">Max Users</label>
                      <input
                        {...register('features_json.max_users')}
                        type="number"
                        className="input"
                        placeholder="-1 for unlimited"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Max Students</label>
                      <input
                        {...register('features_json.max_students')}
                        type="number"
                        className="input"
                        placeholder="-1 for unlimited"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Sessions/Month</label>
                      <input
                        {...register('features_json.max_sessions_per_month')}
                        type="number"
                        className="input"
                        placeholder="-1 for unlimited"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Storage (MB)</label>
                      <input
                        {...register('features_json.storage_mb')}
                        type="number"
                        className="input"
                        placeholder="-1 for unlimited"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="label">Features</label>
                  <div className="space-y-2 mt-1">
                    <div className="flex gap-2">
                      <input
                        {...register('newFeature')}
                        className="input flex-1"
                        placeholder="Add a feature..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addFeature()
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={addFeature}
                        className="btn btn-outline"
                      >
                        Add
                      </button>
                    </div>
                    <div className="space-y-1">
                      {features.map((feature, index) => (
                        <div key={index} className="flex items-center justify-between bg-muted/50 rounded px-3 py-2">
                          <span className="text-sm">{feature}</span>
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="text-destructive hover:text-destructive/80"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    {...register('is_active')}
                    type="checkbox"
                    id="is_active"
                    className="rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium">
                    Active Plan
                  </label>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createPlanMutation.isPending || updatePlanMutation.isPending}
                    className="btn btn-primary"
                  >
                    {createPlanMutation.isPending || updatePlanMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {isCreating ? 'Create Plan' : 'Update Plan'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
